// Lens selector: standard vs prescription lenses, Upload-Lift gating, and the prescription cart add.
//
// Standard lenses: untouched. The theme's product form and s-product-form handle Add to Cart.
// The Upload-Lift fieldset stays disabled, so none of its fields reach that request.
//
// Prescription lenses (only when data-checkout-mode is "live"):
// - The Add to Cart button unlocks only after Upload-Lift has written at least one non-empty
//   `properties[...]` field inside the upload area. Nothing here creates or guesses upload values;
//   Upload-Lift's own field names and values are copied unchanged.
// - One POST to /cart/add.js adds the frame (quantity 1, with the Upload-Lift properties) and the
//   lens upgrade variant (quantity 1), linked by a hidden `_rx_group` id. rx-cart-guard.js keeps the
//   pair complete afterwards.
import { reconcilePrescriptionLines } from '@theme/rx-cart-guard';

const PROPERTY_NAME = /^properties\[(.+)]$/;
const POLL_MS = 750;

class LensSelector extends HTMLElement {
  #ac;
  #poll = null;
  #busy = false;

  connectedCallback() {
    this.#isolateUploadWidget();
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('input', this.#refreshState, { signal });
    this.querySelector('[data-rx-add]')?.addEventListener('click', this.#onAdd, { signal });

    this.#syncMode();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#stopPolling();
  }

  // Keep the Upload-Lift widget in its own inner form (see lens-selector.liquid) so Upload-Lift's
  // click validator binds there, never to the product form's Add to Cart or Buy it now.
  // Normally done by the inline script at parse time; this covers markup re-rendered without scripts.
  #isolateUploadWidget() {
    const host = this.querySelector('[data-rx-upload-host]');
    if (!host || host.querySelector('form[data-rx-upload-form]')) return;
    const form = document.createElement('form');
    form.setAttribute('data-rx-upload-form', '');
    form.noValidate = true;
    form.addEventListener('submit', (event) => event.preventDefault());
    while (host.firstChild) form.appendChild(host.firstChild);
    host.appendChild(form);
  }

  #checkoutAllowed() {
    return this.dataset.checkoutMode === 'live' && Boolean(this.dataset.upgradeVariantId);
  }

  #prescriptionSelected() {
    return Boolean(this.querySelector('[data-lens-prescription]')?.checked);
  }

  #onChange = (event) => {
    if (event.target.matches('input[name="lens_choice"]')) this.#syncMode();
    else this.#refreshState();
  };

  // Enable the Upload-Lift area only while prescription is selected, and watch it for a finished upload.
  #syncMode() {
    const scope = this.querySelector('[data-rx-upload-scope]');
    const prescription = this.#prescriptionSelected();
    if (scope) scope.disabled = !prescription;

    if (prescription && this.#checkoutAllowed()) {
      this.#startPolling();
    } else {
      this.#stopPolling();
    }
    this.#refreshState();
  }

  // Upload-Lift may set hidden input values from script, which fires no events, so poll while active.
  #startPolling() {
    if (this.#poll) return;
    this.#poll = window.setInterval(this.#refreshState, POLL_MS);
  }

  #stopPolling() {
    if (!this.#poll) return;
    window.clearInterval(this.#poll);
    this.#poll = null;
  }

  // Line item properties Upload-Lift has written inside the upload area, copied as-is.
  #uploadProperties() {
    const scope = this.querySelector('[data-rx-upload-scope]');
    if (!scope || scope.disabled) return {};

    const properties = {};
    for (const field of scope.querySelectorAll('input[name], select[name], textarea[name]')) {
      if (field.disabled) continue;
      if (field instanceof HTMLInputElement && field.type === 'file') continue;
      if (field instanceof HTMLInputElement && (field.type === 'checkbox' || field.type === 'radio') && !field.checked) continue;

      const match = field.name.match(PROPERTY_NAME);
      if (!match) continue;
      const value = String(field.value || '').trim();
      if (value) properties[match[1]] = value;
    }
    return properties;
  }

  #refreshState = () => {
    const button = this.querySelector('[data-rx-add]');
    if (!button || !this.#checkoutAllowed()) return;

    const hasUpload = this.#prescriptionSelected() && Object.keys(this.#uploadProperties()).length > 0;
    button.disabled = this.#busy || !hasUpload;

    const hint = this.querySelector('[data-rx-hint]');
    if (hint) {
      const message = hasUpload ? this.dataset.msgReady : this.dataset.msgUploadNeeded;
      if (hint.textContent !== message) hint.textContent = message || '';
    }
  };

  #frameVariantId() {
    const form = this.closest('form');
    const input = form?.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input.value : '';
  }

  #groupId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  #onAdd = async () => {
    if (this.#busy || !this.#checkoutAllowed() || !this.#prescriptionSelected()) return;

    const uploadProperties = this.#uploadProperties();
    const frameVariantId = Number(this.#frameVariantId());
    const upgradeVariantId = Number(this.dataset.upgradeVariantId);
    if (!Object.keys(uploadProperties).length || !frameVariantId || !upgradeVariantId) {
      this.#refreshState();
      return;
    }

    const group = this.#groupId();
    const frameTitle = this.dataset.frameTitle || '';
    const items = [
      {
        id: frameVariantId,
        quantity: 1,
        properties: {
          ...uploadProperties,
          Lenses: this.dataset.lensLabel || 'Prescription Blue Light Lenses',
          _rx_group: group,
        },
      },
      {
        id: upgradeVariantId,
        quantity: 1,
        properties: {
          'For frame': frameTitle,
          _rx_group: group,
          _rx_frame_variant: String(frameVariantId),
        },
      },
    ];

    this.#setBusy(true);
    this.#showError('');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        let message = this.dataset.msgError;
        try {
          const data = await response.json();
          message = data.description || data.message || message;
        } catch {
          // Keep the generic message.
        }
        // If only one line made it in, the guard removes it so no half pair remains.
        await reconcilePrescriptionLines().catch(() => {});
        throw new Error(message);
      }

      await reconcilePrescriptionLines().catch(() => {});
      await customElements.whenDefined('s-cart');
      await customElements.get('s-cart')?.refreshMountedCarts?.();
      await window.Shopify?.actions?.openCart?.();
    } catch (error) {
      this.#showError(error?.message || this.dataset.msgError);
    } finally {
      this.#setBusy(false);
    }
  };

  #setBusy(busy) {
    this.#busy = busy;
    const button = this.querySelector('[data-rx-add]');
    if (button) button.setAttribute('aria-busy', busy ? 'true' : 'false');
    this.#refreshState();
  }

  #showError(message) {
    const error = this.querySelector('[data-rx-error]');
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }
}

if (!customElements.get('s-lens-selector')) customElements.define('s-lens-selector', LensSelector);
